import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/** Cart icon compatible with the native and Sternify cart counters. */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText'];

  get currentCartCount() {
    return parseInt((this.refs.cartBubbleCount ?? this.refs.cartBubbleText).textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    (this.refs.cartBubbleCount ?? this.refs.cartBubbleText).textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow = (event) => {
    if (event.persisted) this.ensureCartBubbleIsCorrect();
  };

  onCartUpdate = (event) => {
    event.promise?.then(({ cart, detail }) => {
      this.renderCartBubble(cart?.totalQuantity ?? detail?.itemCount ?? 0);
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
    });
  };

  renderCartBubble = async (itemCount, animate = true) => {
    const { cartBubble, cartBubbleText } = this.refs;
    const count = this.refs.cartBubbleCount ?? cartBubbleText;
    if (!cartBubble || !cartBubbleText || !count) return;
    count.classList.toggle('hidden', itemCount === 0);
    cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.currentCartCount = itemCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    sessionStorage.setItem('cart-count', JSON.stringify({ value: String(this.currentCartCount), timestamp: Date.now() }));
    if (!animate || itemCount === 0) return;
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (!this.isConnected) return;
    cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(cartBubbleText);
    cartBubble.classList.remove('cart-bubble--animating');
  };

  ensureCartBubbleIsCorrect = () => {
    const count = this.refs.cartBubbleCount ?? this.refs.cartBubbleText;
    if (!count) return;
    const stored = sessionStorage.getItem('cart-count');
    if (stored === null) return;
    try {
      const { value, timestamp } = JSON.parse(stored);
      if (value === count.textContent) return;
      if (Date.now() - timestamp < 10000) {
        const itemCount = parseInt(value, 10);
        if (itemCount >= 0) this.renderCartBubble(itemCount, false);
      }
    } catch (_) {}
  };
}

if (!customElements.get('cart-icon')) customElements.define('cart-icon', CartIcon);
